import { ArrowBack as ArrowLeft, ChevronRight, Home } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ReactFlowProvider } from '@xyflow/react';

import type { Arc, Character, Event, Location } from '@/lib/types';

import { ArcGraphView } from '@/components/arc/arc-graph-view';
import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import { useNovel } from '@/context/novel-context';
import { useArcDetails } from '@/hooks/use-arc-details';
import { useEntities } from '@/hooks/use-entities';
import { useNarrativeItem } from '@/hooks/use-entity';

export const Route = createFileRoute('/arcs/$id')({
  component: ArcDetailPage,
});

function ArcDetailContent() {
  const { id: arcId } = Route.useParams();
  const { novel } = useNovel();
  const selectedNovelId = novel?.id;

  // Fetch Arc Core
  const { data: arc, isLoading: isLoadingArc } = useNarrativeItem<Arc>('arcs', arcId);

  // Fetch Arc Details
  const { data: arcDetails, isLoading: isLoadingDetails } = useArcDetails(arcId);

  const { data: allEvents, isLoading: isLoadingEvents } = useEntities<Event>('events');
  const { data: locations, isLoading: isLoadingLocations } = useEntities<Location>('locations');
  const { data: characters, isLoading: isLoadingCharacters } = useEntities<Character>('characters');

  if (isLoadingArc || isLoadingDetails || isLoadingEvents || isLoadingLocations || isLoadingCharacters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!arc) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">Arc not found.</Typography>
        <Button component={Link} to="/arcs" startIcon={<ArrowLeft sx={{ fontSize: 18 }} />} sx={{ mt: 2 }}>
          Back to Arcs
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header / Breadcrumbs */}
      <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => window.history.back()} size="small">
            <ArrowLeft sx={{ fontSize: 20 }} />
          </IconButton>

          <Breadcrumbs separator={<ChevronRight sx={{ fontSize: 16 }} />}>
            <MuiLink
              component={Link}
              to="/arcs"
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <Home sx={{ fontSize: 16 }} />
              Arcs
            </MuiLink>
            <Typography color="text.primary" sx={{ fontWeight: 600 }}>
              {arc.name}
            </Typography>
          </Breadcrumbs>
        </Box>
      </Paper>

      {/* Graph Area */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {selectedNovelId && (
          <ReactFlowProvider>
            <ArcGraphView
              arc={arc}
              details={arcDetails || { events: [], edges: [] }}
              allEvents={allEvents || []}
              characters={characters || []}
              locations={locations || []}
              novelId={selectedNovelId}
            />
          </ReactFlowProvider>
        )}
      </Box>
    </Box>
  );
}


export default function ArcDetailPage() {
  return (
    <AuthGuard>
      <FeatureGuard featureKey="should_display_arcs_page">
        <ArcDetailContent />
      </FeatureGuard>
    </AuthGuard>
  );
}
